// Tableau de suivi de l'organisation des chantiers : accès aux données.
//
// Les COLONNES vivent dans `chantier_prep_colonnes` (migration 19) ; les CASES
// cochées restent là où elles ont toujours été, dans `chantiers.metadata.prep`,
// repérées par la clé de la colonne. Rien à reprendre de l'existant.
//
// Repli volontaire : tant que la migration 19 n'est pas passée sur le serveur,
// la table n'existe pas. Plutôt que de casser l'écran, on retombe sur les cinq
// étapes historiques, en lecture seule. Le CRM reste utilisable, seul l'ajout
// de colonne est refusé — avec un message qui dit quoi faire.

import {
  AUCUN_FAIT,
  cleDepuisLabel,
  type ColonneSuivi,
  colonneAutomatique,
  type FaitsChantier,
  type SourceColonne,
  type StatutPrep,
} from '@imp/shared/suivi-chantiers';

import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

/** Les cinq étapes d'origine, écrites en dur avant la migration 19. */
export const COLONNES_DEFAUT: ColonneSuivi[] = [
  {
    id: 'defaut-vt',
    cle: 'vt',
    label: 'Visite technique',
    alerte: 'Visite technique à réaliser',
    ordre: 10,
    actif: true,
    source: 'manuel',
  },
  {
    id: 'defaut-occupation_sol',
    cle: 'occupation_sol',
    label: "Demande d'occupation du sol",
    alerte: "Demande d'occupation du sol à faire",
    ordre: 20,
    actif: true,
    source: 'manuel',
  },
  {
    id: 'defaut-dp_mairie',
    cle: 'dp_mairie',
    label: 'Déclaration préalable en mairie',
    alerte: 'Déclaration préalable en mairie à déposer',
    ordre: 30,
    actif: true,
    source: 'manuel',
  },
  {
    id: 'defaut-acompte',
    cle: 'acompte',
    label: 'Acompte',
    alerte: 'Acompte à demander / encaisser',
    ordre: 40,
    actif: true,
    // Depuis la migration 21, le CRM sait émettre un acompte : il sait répondre.
    source: 'acompte',
  },
  {
    id: 'defaut-fourniture',
    cle: 'fourniture',
    label: 'Commande de fournitures',
    alerte: 'Commande de fournitures à passer',
    ordre: 50,
    actif: true,
    source: 'manuel',
  },
];

/** Vrai quand les colonnes affichées viennent du repli, pas de la base. */
export const estColonneDefaut = (c: ColonneSuivi): boolean => c.id.startsWith('defaut-');

interface ColonneRow {
  id: string;
  cle: string;
  label: string;
  alerte: string | null;
  ordre: number;
  actif: boolean;
  /** Absente tant que la migration 22 n'est pas passée : vaut alors « manuel ». */
  source?: string | null;
}

const SOURCES = new Set<string>(['manuel', 'devis_signe', 'acompte', 'facture_emise']);

/** Une source inconnue se comporte en « manuel » : jamais d'écran cassé. */
function versSource(v: string | null | undefined): SourceColonne {
  return typeof v === 'string' && SOURCES.has(v) ? (v as SourceColonne) : 'manuel';
}

/**
 * Les colonnes du tableau. Ne lève JAMAIS : sans la table (migration pas encore
 * passée) ou sans ligne, on rend les colonnes d'origine.
 */
export async function listColonnesSuivi(): Promise<ColonneSuivi[]> {
  // `*` plutôt que la liste des champs : la colonne `source` n'existe pas tant
  // que la migration 22 n'est pas passée, et la nommer ferait échouer la
  // requête entière — donc perdre les colonnes que l'utilisateur a créées.
  const { data, error } = await supabase
    .from('chantier_prep_colonnes')
    .select('*')
    .is('deleted_at', null)
    .order('ordre')
    .overrideTypes<ColonneRow[], { merge: false }>();
  if (error || data.length === 0) return COLONNES_DEFAUT;
  return data.map((c) => ({
    id: c.id,
    cle: c.cle,
    label: c.label,
    alerte: c.alerte,
    ordre: c.ordre,
    actif: c.actif,
    source: versSource(c.source),
  }));
}

const MIGRATION_ABSENTE =
  'Les colonnes ne sont pas encore installées sur le serveur (migration 19). Le tableau reste consultable, mais on ne peut pas encore le modifier.';

/** Ajoute une colonne au tableau. La clé est dérivée du libellé, une fois pour toutes. */
export async function creerColonneSuivi(label: string, alerte: string | null): Promise<void> {
  const existantes = await listColonnesSuivi();
  if (existantes.every(estColonneDefaut)) throw new Error(MIGRATION_ABSENTE);
  const nom = label.trim();
  if (nom === '') throw new Error('Donnez un nom à la colonne.');
  const cle = cleDepuisLabel(nom, existantes.map((c) => c.cle));
  const ordre = Math.max(0, ...existantes.map((c) => c.ordre)) + 10;
  const { error } = await supabase.from('chantier_prep_colonnes').insert({
    organization_id: ORG_ID,
    cle,
    label: nom,
    alerte: alerte?.trim() === '' ? null : (alerte?.trim() ?? null),
    ordre,
    actif: true,
  });
  if (error) throw new Error(error.message);
}

export async function renommerColonneSuivi(id: string, label: string): Promise<void> {
  const nom = label.trim();
  if (nom === '') throw new Error('Donnez un nom à la colonne.');
  const { error } = await supabase
    .from('chantier_prep_colonnes')
    .update({ label: nom })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Masque ou réaffiche une colonne. On ne supprime pas : les cases déjà cochées
 * resteraient orphelines dans les chantiers, et une colonne réaffichée doit
 * retrouver son historique.
 */
export async function activerColonneSuivi(id: string, actif: boolean): Promise<void> {
  const { error } = await supabase.from('chantier_prep_colonnes').update({ actif }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Déplace une colonne d'un cran, en échangeant son rang avec la voisine. */
export async function deplacerColonneSuivi(id: string, sens: -1 | 1): Promise<void> {
  const colonnes = (await listColonnesSuivi()).slice().sort((a, b) => a.ordre - b.ordre);
  const i = colonnes.findIndex((c) => c.id === id);
  const courante = colonnes[i];
  const voisine = colonnes[i + sens];
  if (!courante || !voisine) return; // déjà au bout : rien à faire
  const { error } = await supabase.from('chantier_prep_colonnes').upsert([
    { id: courante.id, ordre: voisine.ordre },
    { id: voisine.id, ordre: courante.ordre },
  ]);
  if (error) throw new Error(error.message);
}

/** Un chantier tel que le tableau le lit. */
export interface ChantierSuiviRow {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  contact_id: string | null;
  ville: string | null;
  date_prevue_debut: string | null;
  metadata: { prep?: Partial<Record<string, StatutPrep>> } | null;
}

export async function listChantiersSuivi(): Promise<ChantierSuiviRow[]> {
  const { data, error } = await supabase
    .from('chantiers')
    .select('id,numero,libelle,statut,contact_id,ville,date_prevue_debut,metadata')
    .is('deleted_at', null)
    .overrideTypes<ChantierSuiviRow[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

/**
 * Coche une case. On relit le chantier juste avant d'écrire : `metadata` porte
 * bien d'autres réglages (nacelle, commercial, montants) qu'un remplacement en
 * bloc effacerait.
 */
export async function setStatutCase(
  chantierId: string,
  colonne: ColonneSuivi,
  statut: StatutPrep,
): Promise<void> {
  if (colonneAutomatique(colonne)) {
    throw new Error(
      `« ${colonne.label} » se remplit tout seul d'après le CRM : cette case ne se coche pas.`,
    );
  }
  const cle = colonne.cle;
  const { data, error } = await supabase
    .from('chantiers')
    .select('metadata')
    .eq('id', chantierId)
    .single()
    .overrideTypes<{ metadata: Record<string, unknown> | null }, { merge: false }>();
  if (error) throw new Error(error.message);
  const metadata = data.metadata ?? {};
  const prep = { ...((metadata.prep ?? {}) as Record<string, StatutPrep>), [cle]: statut };
  const { error: e2 } = await supabase
    .from('chantiers')
    .update({ metadata: { ...metadata, prep } })
    .eq('id', chantierId);
  if (e2) throw new Error(e2.message);
}

// ----- Ce que le CRM sait des chantiers --------------------------------------

/**
 * Les faits, chantier par chantier, qui remplissent les colonnes automatiques.
 *
 * Ne lève jamais : un serveur en retard d'une migration, ou une table
 * momentanément inaccessible, ne doit pas vider le tableau de suivi. On rend
 * alors « rien de connu », et les colonnes automatiques se taisent.
 */
export async function chargerFaitsChantiers(): Promise<Map<string, FaitsChantier>> {
  const faits = new Map<string, FaitsChantier>();
  const pour = (id: string): FaitsChantier => {
    const f = faits.get(id) ?? { ...AUCUN_FAIT };
    faits.set(id, f);
    return f;
  };

  const devis = await supabase
    .from('devis')
    .select('chantier_id,statut')
    .not('chantier_id', 'is', null)
    .is('deleted_at', null)
    .overrideTypes<{ chantier_id: string; statut: string }[], { merge: false }>();
  if (!devis.error) {
    for (const d of devis.data) {
      const f = pour(d.chantier_id);
      f.suiviParLeCrm = true;
      if (d.statut === 'signe' || d.statut === 'converti_facture') f.devisSigne = true;
    }
  }

  const factures = await supabase
    .from('factures')
    .select('chantier_id,kind,statut')
    .not('chantier_id', 'is', null)
    .is('deleted_at', null)
    .overrideTypes<{ chantier_id: string; kind: string; statut: string }[], { merge: false }>();
  if (!factures.error) {
    for (const fa of factures.data) {
      const f = pour(fa.chantier_id);
      f.suiviParLeCrm = true;
      // Un brouillon ne prouve rien, un avoir annule, une facture annulée non plus.
      const emise =
        fa.statut !== 'brouillon' && fa.statut !== 'annulee_avoir' && fa.kind !== 'avoir';
      if (!emise) continue;
      f.factureEmise = true;
      if (fa.kind === 'acompte') f.acompteEmis = true;
      // Une facture de solde ou une facture entière vaut acompte franchi :
      // on ne va pas réclamer l'acompte d'un chantier déjà facturé.
      if (fa.kind === 'finale' || fa.kind === 'standard') f.acompteEmis = true;
    }
  }

  return faits;
}

/** Les cases cochées à la main d'un chantier, remises à zéro. */
export async function viderPrepChantier(chantierId: string): Promise<void> {
  const { data, error } = await supabase
    .from('chantiers')
    .select('metadata')
    .eq('id', chantierId)
    .single()
    .overrideTypes<{ metadata: Record<string, unknown> | null }, { merge: false }>();
  if (error) throw new Error(error.message);
  const metadata = { ...(data.metadata ?? {}) };
  delete metadata.prep;
  const { error: e2 } = await supabase
    .from('chantiers')
    .update({ metadata })
    .eq('id', chantierId);
  if (e2) throw new Error(e2.message);
}
